import { useCallback, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  emailSchema,
  storeConfigSelector,
  useLoadingContext,
  useSubscribeEmailMutation,
  useToastContext,
  z,
} from '@segway/core'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useForm } from 'react-hook-form'

import reportGTMEvent from '@/utils/gtm'

/**
 * 订阅 Hooks
 */
const useSubscribeForm = (leadSource: string = '') => {
  const storeConfig = useAppSelector(storeConfigSelector)
  const [subscribeEmail, { isLoading }] = useSubscribeEmailMutation()

  const toast = useToastContext()
  const loading = useLoadingContext()

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  })

  const validateEmail = (email: string) => {
    try {
      emailSchema.parse({ email })

      return { valid: true, error: null }
    } catch (err) {
      if (err instanceof z.ZodError) {
        return { valid: false, error: err.errors[0].message }
      }
      return { valid: false, error: 'Invalid email address' }
    }
  }

  const onSubmit = useCallback(
    async ({ email }: z.infer<typeof emailSchema>) => {
      const { valid, error } = validateEmail(email)

      if (!valid && error) {
        toast.show({
          icon: 'fail',
          content: error,
        })

        return
      }

      loading.show()

      try {
        const resp = await subscribeEmail({
          email,
        }).unwrap()

        if (resp && resp.subscribeEmailToNewsletter?.status) {
          /**
           * GTM: generate_lead
           */
          reportGTMEvent({
            event: 'generate_lead',
            ecommerce: {
              currency: storeConfig?.default_display_currency_code || '',
              value: '20.00', // 固定写死 20
              lead_source: leadSource,
            },
          })
          toast.show({
            icon: 'success',
            content: 'Gracias por tu suscripción.',
          })
        }
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: (e as { data: string })?.data ?? '',
        })
      } finally {
        loading.hide()
      }
    },
    [loading, subscribeEmail, toast, storeConfig, leadSource],
  )

  useEffect(() => {
    if (errors?.email?.message) {
      toast.show({
        icon: 'fail',
        content: errors?.email?.message,
      })
    }
  }, [errors?.email, toast])

  return { handleSubmit, isLoading, errors, register, onSubmit }
}

export default useSubscribeForm
